import { useState, useEffect, useCallback } from "react";
import { Button, Space, Select, Input, DatePicker, Tag, Modal, message } from "antd";
import { ReloadOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import DataTable from "../../components/common/DataTable";
import KPICard, { KPIGrid } from "../../components/common/KPICard";
import ExportButton from "../../components/common/ExportButton";
import { PartnerLabel } from "../../components/common/DeletedTag";
import usePermission from "../../hooks/usePermission";
import { SALARY_STATUS_OPTIONS, formatOmr } from "./constants";
import {
  listSalaryStatus, getSalaryStatusKpis, approveSalary, rejectSalary,
  exportSalaryStatus,
} from "../../services/payroll.service";

const STATUS_COLORS = {
  pending: "gold",
  approved: "green",
  transferred: "green",
  onHold: "orange",
  rejected: "red",
};

/**
 * Salary Status — the approval view over EmployeeSalary, filtered server-side to
 * rider payees. Records are entered on Accounts › Employee Salary; this tab only
 * approves or rejects them.
 */
export default function SalaryStatusTab() {
  const { canApprove } = usePermission("payrollExpenses");

  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState();
  const [month, setMonth] = useState(null);
  const [actingId, setActingId] = useState(null);

  const params = useCallback(
    (extra = {}) => ({
      search: search || undefined,
      status: status || undefined,
      month: month ? month.month() + 1 : undefined,
      year: month ? month.year() : undefined,
      ...extra,
    }),
    [search, status, month]
  );

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, kpiResponse] = await Promise.all([
        listSalaryStatus(params({ page, per_page: pageSize })),
        getSalaryStatusKpis(params()),
      ]);
      setRows(list?.data?.records || []);
      setTotalCount(list?.data?.pagination?.total_count || 0);
      setKpis(kpiResponse?.data || {});
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not load salary status");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [params, page, pageSize]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    setPage(1);
  }, [search, status, month]);

  const handleApprove = async (record) => {
    setActingId(record._id);
    try {
      await approveSalary(record._id);
      message.success("Salary approved");
      fetchAll();
    } catch (err) {
      message.error(err?.errors?.[0]?.msg || "Could not approve salary");
    } finally {
      setActingId(null);
    }
  };

  const handleReject = (record) => {
    let reason = "";
    Modal.confirm({
      title: "Reject Salary",
      content: (
        <Input.TextArea
          rows={3}
          placeholder="Reason for rejection"
          onChange={(e) => {
            reason = e.target.value;
          }}
        />
      ),
      okText: "Reject",
      okType: "danger",
      onOk: async () => {
        if (!reason.trim()) {
          message.error("Rejection reason is required");
          return Promise.reject(new Error("reason required"));
        }
        try {
          await rejectSalary(record._id, reason.trim());
          message.success("Salary rejected");
          fetchAll();
        } catch (err) {
          message.error(err?.errors?.[0]?.msg || "Could not reject salary");
        }
      },
    });
  };

  const columns = [
    {
      title: "Partner",
      dataIndex: "partnerName",
      render: (_, r) => (
        <PartnerLabel name={`${r.partnerId} — ${r.partnerName}`} deleted={r.riderDeleted} />
      ),
    },
    { title: "Salary Month", dataIndex: "salaryMonthLabel" },
    { title: "Worked Days", dataIndex: "workedDays", align: "right" },
    { title: "Basic Pay", dataIndex: "basicPay", align: "right", render: formatOmr },
    { title: "Allowances", key: "allowances", align: "right",
      render: (_, r) => formatOmr(Number(r.hra || 0) + Number(r.tra || 0) + Number(r.otherAllowances || 0)) },
    { title: "Deductions", key: "deductions", align: "right",
      render: (_, r) => formatOmr(Number(r.deductionLop || 0) + Number(r.otherDeductions || 0) + Number(r.socialSecurityDeductions || 0)) },
    { title: "Net Pay (OMR)", dataIndex: "netPay", align: "right", render: formatOmr },
    {
      title: "Status",
      dataIndex: "status",
      render: (value, r) => (
        <Tag color={STATUS_COLORS[value] || "default"}>{r.statusLabel}</Tag>
      ),
    },
    { title: "Approved By", dataIndex: "approvedByName" },
    ...(canApprove
      ? [
          {
            title: "Action",
            key: "decision",
            width: 170,
            render: (_, record) =>
              record.status === "pending" ? (
                <Space size="small">
                  <Button
                    size="small"
                    type="primary"
                    icon={<CheckOutlined />}
                    loading={actingId === record._id}
                    onClick={() => handleApprove(record)}
                  >
                    Approve
                  </Button>
                  <Button
                    size="small"
                    danger
                    icon={<CloseOutlined />}
                    onClick={() => handleReject(record)}
                  >
                    Reject
                  </Button>
                </Space>
              ) : null,
          },
        ]
      : []),
  ];

  return (
    <div>
      <KPIGrid minWidth={150}>
        <KPICard label="All" value={kpis.all} />
        <KPICard label="Pending" value={kpis.pending} />
        <KPICard label="Approved" value={kpis.approved} tone="positive" />
        <KPICard label="Transferred" value={kpis.transferred} tone="positive" />
        <KPICard label="On Hold" value={kpis.onHold} />
        <KPICard label="Rejected" value={kpis.rejected} tone="negative" />
        <KPICard label="Net Payable" value={kpis.netPayable} unit="OMR" tone="dark" />
      </KPIGrid>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search partner"
          allowClear
          style={{ width: 240 }}
          onSearch={setSearch}
        />
        <Select
          placeholder="Status"
          allowClear
          style={{ width: 160 }}
          options={SALARY_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
        />
        <DatePicker picker="month" format="MM/YYYY" onChange={setMonth} placeholder="Salary month" />
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>
          Refresh
        </Button>
        <ExportButton
          moduleName="Salary_Status"
          onExport={() => exportSalaryStatus(params())}
        />
      </Space>

      <DataTable
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{ current: page, pageSize, total: totalCount }}
        onPageChange={setPage}
      />
    </div>
  );
}
